import React from 'react';
import { Box, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { OnboardingAction, OnboardingState } from '../onboarding.types';

interface Step4Props {
  state: OnboardingState;
  dispatch: React.Dispatch<OnboardingAction>;
}

const Step4_FeeStructure: React.FC<Step4Props> = ({ state, dispatch }) => {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.includes('fr');

  const studentFeeTypes = useSelector((state: RootState) =>
    state.feeTypes.student.concat(state.feeTypes.other)
  );
  const resolveLabel = (key: string) => {
    const found = studentFeeTypes.find((ft) => ft.enum_key === key);
    if (found) return isFr ? found.value_fr : found.value_en;
    return key;
  };

  // Derive unique fee categories from the classrooms added in step 3
  const uniqueCategories = Array.from(new Set(state.classrooms.map((c) => c.feeCategory)));

  const getAmount = (category: string): string => {
    const entries = state.feeStructure[category];
    if (entries && entries.length > 0) return String(entries[0].amount);
    return '';
  };

  const handleAmountChange = (category: string, value: string) => {
    const amount = parseFloat(value) || 0;
    const updated = {
      ...state.feeStructure,
      [category]: [{ feeType: category, amount }],
    };
    dispatch({ type: 'SET_FEE_STRUCTURE', payload: updated });
  };

  if (uniqueCategories.length === 0) {
    return (
      <Typography color="text.secondary" variant="body2">
        {t('onboarding.no_fee_categories')}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('onboarding.fee_structure_hint')}
      </Typography>
      <Stack spacing={2}>
        {uniqueCategories.map((category) => (
          <Box
            key={category}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
          >
            <Typography variant="body1" flex={1}>
              {resolveLabel(category)}
            </Typography>
            <TextField
              size="small"
              label={t('onboarding.fee_amount')}
              type="number"
              value={getAmount(category)}
              onChange={(e) => handleAmountChange(category, e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{ width: 160 }}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default Step4_FeeStructure;
